import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import { useHistory } from 'react-router-dom';
import { Form } from 'semantic-ui-react';

import {
	handleError,
	log
} from '../../../services/utils';

import {
	getActivityRoute,
	getActivityList
} from '../../../api/nora/activities';

import { useAuthState } from '../../../contexts/AuthContext';
import {
	useDashboardState,
	useDashboardAction
} from '../../../contexts/DashboardContext';
import { useUIAction } from '../../../contexts/UIContext';

import ActivityLogActions from './ActivityLogActions';
import CalendarSummary from './CalendarSummary';
import DateTimeCell from '../../Table/Cells/DateTimeCell';
import dateTimeFilterFn from '../../Table/filterFunctions/dateTimeFilterFn';
import HelpModal from '../../Shared/HelpModal';
import multiWordFilterFn from '../../Table/filterFunctions/multiWordFilterFn';
import PageHeader from '../../Shared/PageHeader';
import Table from '../../Table';

import styles from './dashboard.module.css';
import SimpleChart from './SimpleChart';
import StackedBarChart from './StackedBarChart';

const ActivityLog = () => {
	const history = useHistory();

	const {
		activityList,
		initialized,
		isInSync,
		tableState
	} = useDashboardState();
	const {
		setActivityList,
		setIsInSync,
		setTableState
	} = useDashboardAction();
	const {
		setApiErrorAlert,
		setInfobarLoader,
		setScreenLoader
	} = useUIAction();
	const { user } = useAuthState();

	const [ isLoading, setIsLoading ] = useState( true );
	const [ isTableLoading, setIsTableLoading ] = useState( true );
	const [ includeComplete, setIncludeComplete ] = useState( false );
	const [ includeZZZ, setIncludeZZZ ] = useState( false );

	const sortActivities = useCallback( activities => {
		activities.sort( ( a, b ) => {
			if ( a.activityStatus.id !== b.activityStatus.id ) {
				if ( a.activityStatus.id === 76 ) {
					return 1;
				} else if ( b.activityStatus.id === 76 ) {
					return -1;
				}
			}

			return new Date( b.createdOn ) - new Date( a.createdOn );
		} );
	}, [] );

	const retrieveActivityLog = useCallback( async () => {
		setInfobarLoader( 'Retrieving Activity Log...' );

		try {
			const data = await getActivityList( {
				includeComplete,
				includeZZZ
			} );

			sortActivities( data );

			setActivityList( data );
			setIsInSync( true );
		} catch ( error ) {
			setApiErrorAlert( error );
			throw error;
		}
	}, [
		includeComplete,
		includeZZZ,
		setActivityList,
		setApiErrorAlert,
		setInfobarLoader,
		setIsInSync,
		sortActivities
	] );

	useEffect( () => {
		( async () => {
			if ( !initialized ) {
				setInfobarLoader( 'Please wait...' );
			} else {
				setIsTableLoading( true );

				try {
					await retrieveActivityLog();
					setInfobarLoader( '' );
				} catch ( error ) {
					if ( error.message !== 'canceled' ) {
						handleError( error );
						setInfobarLoader( '' );
					}
				}

				setIsLoading( false );
				setIsTableLoading( false );
			}
		} )();
	}, [
		initialized,
		retrieveActivityLog,
		setInfobarLoader
	] );

	const backgroundRefresh = useCallback( async () => {
		try {
			const data = await getActivityList( {
				includeComplete,
				includeZZZ
			} );

			sortActivities( data );

			setIsInSync( true );

			setActivityList( data );
		} catch ( error ) {
			setApiErrorAlert( error );
		}
	}, [
		includeComplete,
		includeZZZ,
		setActivityList,
		setApiErrorAlert,
		setIsInSync,
		sortActivities
	] );

	useEffect( () => {
		if ( !isInSync ) {
			backgroundRefresh();
		}
	}, [
		backgroundRefresh,
		isInSync
	] );

	const dateCell = useCallback( ( { row } ) => <DateTimeCell date={ row.original.createdOn } />, [] );

	const columns = useMemo( () => [
		{
			accessor: 'createdOn',
			Header: 'Date',
			filter: dateTimeFilterFn,
			Cell: dateCell,
			width: 40,
			maxWidth: 60
		},
		{
			accessor: 'label',
			Header: 'Activity',
			filter: multiWordFilterFn,
			width: 40,
			maxWidth: 40,
			minWidth: 40
		},
		{
			accessor: 'createdBy',
			Header: 'User',
			filter: multiWordFilterFn,
			width: 40,
			maxWidth: 40,
			minWidth: 40
		},
		{
			accessor: 'entry',
			Header: 'Entry',
			filter: multiWordFilterFn,
			minWidth: 100
		}
	], [ dateCell ] );

	const handleView = useCallback( async id => {
		setScreenLoader( 'Please wait...' );
		const {
			route,
			navigationState
		} = await getActivityRoute( id );

		if ( route ) {
			history.push( {
				pathname: route,
				state: {
					...navigationState,
					text: '\u2190\u00A0\u00A0Back to Dashboard',
					to: '/dashboard'
				}
			} );
		}

		setScreenLoader( '' );
	}, [ history, setScreenLoader ] );

	const rowStyles = useMemo( () => [
		{
			style: { fontWeight: 600 },
			condition: ( { priority } ) => priority === 1
		}
	], [] );

	const greeting = useMemo( () => {
		const hour = new Date().getHours();

		if ( hour < 12 ) {
			return 'Good morning, ';
		} else if ( hour < 17 ) {
			return 'Good afternoon, ';
		}

		return 'Good evening, ';
	}, [] );

	if ( isLoading ) {
		return null;
	}

	return (
		<div className="page">
			{ log( 'activityLog' ) }
			<PageHeader
				actions={ [] }
				title={ <>
					<span>{ greeting }{ user.profile.firstName }</span>
					<HelpModal
						id={ 1 }
						triggerStyles={ { verticalAlign: 'top' } }
					/>
				</>
				}
			/>

			<div className={ styles.chartContainer }>
				<CalendarSummary />
				<SimpleChart />
				<StackedBarChart />
			</div>

			<PageHeader
				actions={ [] }
				title={ <>
					<span>Activity Log</span>
					<HelpModal
						id={ 2 }
						triggerStyles={ { verticalAlign: 'top' } }
					/>
				</>
				}
			/>
			<Form>
				<Form.Group>
					<Form.Checkbox
						checked={ includeComplete }
						label="Include Completed"
						onChange={ ( e, { checked } ) => setIncludeComplete( checked )}
					/>
					<Form.Checkbox
						checked={ includeZZZ }
						label="Include Snoozed"
						onChange={ ( e, { checked } ) => setIncludeZZZ( checked )}
					/>
				</Form.Group>
			</Form>
			<Table
				currentRowRight={ row => (
					<ActivityLogActions
						includeComplete={ includeComplete }
						includeZZZ={ includeZZZ }
						refresh={ backgroundRefresh }
						row={ row }
					/>
				) }
				columns={ columns }
				data={ activityList }
				highlightCurrentRow
				isLoading={ isTableLoading }
				onRowClick={ handleView }
				rowStyles={ rowStyles }
				setTableState={ setTableState }
				tableState={ tableState }
			/>
		</div>
	);
};

export default memo( ActivityLog );
